import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CalendarDays, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SEV_BG = (sev, count) => {
    // Heatmap intensity: severity * count
    if (count === 0) return 'bg-muted/30';
    if (sev >= 8 || count >= 4) return 'bg-rose-500 text-white';
    if (sev >= 6 || count >= 3) return 'bg-orange-400 text-white';
    if (sev >= 4 || count >= 2) return 'bg-amber-300';
    return 'bg-emerald-200';
};

const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function SymptomCalendar() {
    const { language } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(90);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/health/symptoms/calendar?days=${days}`);
                setData(res.data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [days]);

    if (loading) return <Card className="animate-pulse"><CardContent className="h-40" /></Card>;
    if (!data) return null;

    // Build day-grid for the last `days` days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const byDateMap = {};
    (data.by_date || []).forEach(d => { byDateMap[d.date] = d; });

    const cells = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const ds = d.toISOString().slice(0, 10);
        cells.push({ date: ds, ...(byDateMap[ds] || { count: 0, max_severity: 0, categories: [], symptoms: [] }) });
    }

    // Pad start so first cell starts on Monday
    const firstWeekday = (new Date(cells[0].date).getDay() + 6) % 7; // 0 = Mon
    const padded = Array(firstWeekday).fill(null).concat(cells);

    const weeks = [];
    for (let i = 0; i < padded.length; i += 7) weeks.push(padded.slice(i, i + 7));

    const t = (de, en) => language === 'de' ? de : en;

    return (
        <Card data-testid="symptom-calendar">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-rose-500" />
                        {t('Symptom-Kalender', 'Symptom calendar')}
                    </span>
                    <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value))}
                        className="text-xs h-7 px-2 rounded-md border border-input bg-background"
                        data-testid="symptom-calendar-range"
                    >
                        <option value={30}>30 {t('Tage', 'days')}</option>
                        <option value={90}>90 {t('Tage', 'days')}</option>
                        <option value={180}>180 {t('Tage', 'days')}</option>
                    </select>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-lg bg-rose-50 dark:bg-rose-950/30">
                        <p className="text-lg font-bold text-rose-600">{data.total_entries}</p>
                        <p className="text-[10px] text-muted-foreground">{t('Einträge', 'entries')}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30">
                        <p className="text-lg font-bold text-amber-600">{data.days_with_symptoms}</p>
                        <p className="text-[10px] text-muted-foreground">{t('Tage betroffen', 'days affected')}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                        <p className="text-lg font-bold text-emerald-600">{Math.max(0, days - data.days_with_symptoms)}</p>
                        <p className="text-[10px] text-muted-foreground">{t('Beschwerdefrei', 'symptom-free')}</p>
                    </div>
                </div>

                {/* Heatmap grid */}
                <div className="flex gap-1 overflow-x-auto pb-1">
                    <div className="flex flex-col gap-1 pr-1 text-[9px] text-muted-foreground pt-0">
                        {(language === 'de' ? WEEKDAYS_DE : WEEKDAYS_EN).map((w, i) => (
                            <div key={i} className="h-3.5 leading-3.5">{i % 2 === 0 ? w : ''}</div>
                        ))}
                    </div>
                    {weeks.map((week, wi) => (
                        <div key={wi} className="flex flex-col gap-1">
                            {Array(7).fill(null).map((_, di) => {
                                const cell = week[di];
                                if (!cell) return <div key={di} className="w-3.5 h-3.5" />;
                                const cls = SEV_BG(cell.max_severity, cell.count);
                                return (
                                    <div
                                        key={di}
                                        className={`w-3.5 h-3.5 rounded-sm cursor-pointer ${cls} hover:ring-2 hover:ring-primary transition`}
                                        title={`${cell.date}: ${cell.count} ${t('Symptome', 'symptoms')}${cell.max_severity ? `, max ${cell.max_severity}/10` : ''}`}
                                        onMouseEnter={() => cell.count > 0 && setHover(cell)}
                                        onMouseLeave={() => setHover(null)}
                                        onClick={() => cell.count > 0 && setHover(cell)}
                                        data-testid={`symptom-day-${cell.date}`}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{t('Weniger', 'Less')}</span>
                    <div className="w-3 h-3 rounded-sm bg-muted/30" />
                    <div className="w-3 h-3 rounded-sm bg-emerald-200" />
                    <div className="w-3 h-3 rounded-sm bg-amber-300" />
                    <div className="w-3 h-3 rounded-sm bg-orange-400" />
                    <div className="w-3 h-3 rounded-sm bg-rose-500" />
                    <span>{t('Mehr', 'More')}</span>
                </div>

                {/* Hover detail */}
                {hover && (
                    <div className="p-3 rounded-lg bg-muted/40 border border-border" data-testid="symptom-detail">
                        <p className="text-xs font-semibold mb-1">
                            {new Date(hover.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                            <span className="ml-2 text-muted-foreground">{hover.count} {t('Symptome', 'symptoms')}</span>
                        </p>
                        <ul className="space-y-0.5">
                            {hover.symptoms.map((s, i) => (
                                <li key={i} className="text-[11px] flex items-center gap-1.5">
                                    <AlertCircle className="w-3 h-3 text-rose-400 shrink-0" />
                                    <span className="font-medium">{s.symptom}</span>
                                    <span className="text-muted-foreground">({s.category}, {s.severity}/10)</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Top categories */}
                {data.top_categories?.length > 0 && (
                    <div className="pt-2 border-t border-border">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
                            {t('Häufigste Kategorien', 'Most frequent categories')}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {data.top_categories.slice(0, 6).map(c => (
                                <span key={c.category} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900">
                                    {c.category} × {c.count}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
